import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC, useMemo } from "react";

import BaseDataTable from "../../../../../components/modules/BaseDataTable";
import { BrandgenCampaignPool, BrandgenCampaignPoolReport, ReportTimeFrames } from "../../../../../types/campaignTypes";
import { transformRowValue } from "../../../../../helpers/campainRowFormatHelpers";
import { truncateText } from "../../../../../helpers/truncateText";
import { useCampaignDashboardStyles } from "../../CampaignDashboardStylesContext";

type CampaignReportDashboardProps = {
  campaign: BrandgenCampaignPool;
  campaignPoolReport: BrandgenCampaignPoolReport | null;
  selectedTimeFrame: ReportTimeFrames;
};

const CampaignReportDashboard: FC<CampaignReportDashboardProps> = ({
  campaignPoolReport,
  campaign,
  selectedTimeFrame,
}) => {
  const classes = useCampaignDashboardStyles();
  const campaignTimeReport =
    selectedTimeFrame === ReportTimeFrames.CUSTOM_DATE
      ? campaignPoolReport || null
      : campaign?.campaignPoolReports?.find((report) => report.timeFrame === selectedTimeFrame) || null;

  const { clicks, ctr, impressions, totalSpend, cpm, cpc, totalConversions } = campaignTimeReport ?? {};

  const campaignBasicInfo = useMemo(
    () => ({
      id: campaign.brandgenCampaignPoolId,
      status:
        campaign.status === "processing" ? (
          <Typography className={classes.processingStatus}>PROCESSING</Typography>
        ) : campaign.active ? (
          <Typography className={classes.activeStatus}>ACTIVE</Typography>
        ) : (
          <Typography className={classes.inactiveStatus}>INACTIVE</Typography>
        ),
      name: (
        <Tooltip title={campaign.name}>
          <Typography>{truncateText(campaign.name, 20)}</Typography>
        </Tooltip>
      ),
      startDate: new Date(campaign.start_date).toLocaleDateString(),
      endDate: new Date(campaign.end_date).toLocaleDateString(),
      campaignBudget: transformRowValue(campaign.campaign_budget, false, true, "CURRENCY"),
      matchedAccounts: +(campaign.accounts ?? 0),
    }),
    [campaign, classes],
  );

  const campaignAllTimeReportInfo = useMemo(
    () => ({
      clicks: transformRowValue(clicks ?? 0, true, false, "FLAT"),
      clickThroughRate: transformRowValue(ctr ?? 0, false, true, "PERCENTAGE"),
      impressions: transformRowValue(impressions ?? 0, true, false, "FLAT"),
      budgetSpent: transformRowValue(totalSpend ?? 0, false, true, "CURRENCY"),
      costPerMile: transformRowValue(cpm ?? 0, false, true, "CURRENCY"),
      costPerClick: transformRowValue(cpc ?? 0, false, true, "CURRENCY"),
      conversions: transformRowValue(totalConversions ?? 0, true, false, "FLAT"),
      clickThroughConversions: transformRowValue(ctr ?? 0, true, false, "FLAT"),
    }),
    [clicks, ctr, impressions, totalSpend, cpm, cpc, totalConversions],
  );

  const rows = [
    {
      ...campaignBasicInfo,
      ...campaignAllTimeReportInfo,
    },
  ];

  const columns = useMemo(
    () => [
      { key: "status", label: "Status", minWidth: 100 },
      { key: "name", label: "Name", minWidth: 120 },
      { key: "startDate", label: "Start Date", minWidth: 120 },
      { key: "endDate", label: "End Date", minWidth: 120 },
      { key: "campaignBudget", label: "Campaign Budget", minWidth: 120 },
      { key: "matchedAccounts", label: "Matched Accounts", minWidth: 70 },
      { key: "clicks", label: "Clicks", minWidth: 100 },
      { key: "clickThroughRate", label: "CTR", minWidth: 100 },
      { key: "impressions", label: "Impressions", minWidth: 70 },
      { key: "budgetSpent", label: "Budget Spent", minWidth: 100 },
      { key: "costPerClick", label: "CPC (USD)", minWidth: 100 },
      { key: "costPerMile", label: "CPM (USD)", minWidth: 100 },
      { key: "conversions", label: "Conversions", minWidth: 100 },
      { key: "ctr", label: "Click Through Conversions", minWidth: 100 },
    ],
    [],
  );

  return (
    <div className={classes.reportDashboard}>
      <BaseDataTable columns={columns} rows={rows} />
    </div>
  );
};

export default CampaignReportDashboard;
